import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  Snackbar, SnackbarContent,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import GridOnIcon from '@mui/icons-material/GridOn';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import React, { useEffect, useState } from 'react';
import { useAPSFolderItems, useAPSRootFolders } from '../../hooks/useAps';
import useMigrateStore from '../../store/useMigrate';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ROOT = { id: 'root', name: 'My Drive' };

const getIcon = (type, fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase();

  // file-based icons
  if (ext === 'pdf' && type === 'items') {
    return <PictureAsPdfIcon color="error" />;
  }

  if ((ext === 'xlsx' || ext === 'xls') && type === 'items') {
    return <GridOnIcon color="success" />;
  }

  // type-based icons
  switch (type) {
    case 'folders':
      return <FolderIcon color="primary" />;
    case 'document':
      return <DescriptionIcon color="info" />;
    case 'presentation':
      return <SlideshowIcon color="warning" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

/* ================= Breadcrumb ================= */

function FolderBreadcrumb({ breadcrumb, onNavigate }) {
  return (
    <Breadcrumbs>
      {breadcrumb.map((item, index) => {
        const isLast = index === breadcrumb.length - 1;

        return isLast ? (
          <Typography key={item.id} fontWeight={500}>
            {item.name}
          </Typography>
        ) : (
          <Link key={item.id} underline="hover" sx={{ cursor: 'pointer' }} onClick={() => onNavigate(index)}>
            {item.name}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

const SelectButton = ({ onClick, endIcon }) => (
  <Button
    size="small"
    variant="contained"
    sx={{
      fontWeight: 'bold'
      // bgcolor: 'success.darker',
      // '&:hover': {
      //   bgcolor: 'success.dark',
      //   color: '#fff'
      // }
    }}
    onClick={onClick}
    endIcon={endIcon}
  >
    Select
  </Button>
);

const ActionMenu = ({ anchorEl, open, onOpen, onClose, onConfirm }) => (
  <>
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(e);
      }}
    >
      <MoreVertIcon />
    </IconButton>

    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem onClick={(e) => onConfirm(e, "upload")}>
        <ListItemIcon>
          <UploadFileIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Upload" />
      </MenuItem>

      <MenuItem onClick={(e) => onConfirm(e, "schedule")}>
        <ListItemIcon>
          <ScheduleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Schedule" />
      </MenuItem>

      <MenuItem onClick={(e) => onConfirm(e, "autos-chedule")}>
        <ListItemIcon>
          <SyncAltIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Automatic Migration" />
      </MenuItem>
    </Menu>
  </>
);

/* ================= Row ================= */

function FolderRow({ item, onOpen, onSelect, mode }) {
  const isFolder = item.type === 'folders';
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const isSource = mode === 'source';
  const isDestination = mode === 'destination';

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleConfirmMigration = (e, param) => {
    e?.stopPropagation();
    setAnchorEl(null);
    onSelect(item, param)
    // console.log(param)
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isFolder ? 'pointer' : 'default',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (isFolder) {
          onOpen(item);
        }
      }}
    >
      {getIcon(item.type, item.attributes.displayName)}

      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 14 }} title={item.name}>
        {item.attributes.displayName}
      </Typography>

      {!isFolder && isSource && <SelectButton onClick={() => onSelect(item)} endIcon={<DescriptionIcon />} />}

      {!isFolder && isDestination && (
        <ActionMenu 
        anchorEl={anchorEl} 
        open={open} 
        onOpen={handleOpen} 
        onClose={handleClose} 
        onConfirm={handleConfirmMigration} />
      )}

      {isFolder && (
        <Stack direction="row" spacing={1}>
          {isSource ? (
            <SelectButton
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
              }}
              endIcon={<FolderIcon />}
            />
          ) : (
            <ActionMenu 
            anchorEl={anchorEl} 
            open={open} 
            onOpen={handleOpen} 
            onClose={handleClose} 
            onConfirm={handleConfirmMigration} />
          )}
        </Stack>
      )}
    </Stack>
  );
}

/* ================= Main Dialog ================= */

export default function ApsFolderMigrationContentDialog({ open, onClose, onConfirm, data }) {
  const { folderMode } = useMigrateStore();
  const [breadcrumb, setBreadcrumb] = useState([{ id: 'root', name: data.project_name }]);

  const currentFolderId = breadcrumb[breadcrumb.length - 1].id;
  console.log(currentFolderId);
  const payload = React.useMemo(
    () => ({
      hub_id: data.hub_id,
      project_id: `b.${data.project_id}`
    }),
    [data.hub_id, data.project_id]
  );

  const { data: rootItems = [], isFetching: isFetchingFolder, isError: isRootError, error: rootError } = useAPSRootFolders(payload);

  const isRoot = currentFolderId === 'root';
  const folderPayload = React.useMemo(() => {
    if (isRoot) return payload;
    return {
      ...payload,
      folder_id: currentFolderId
    };
  }, [isRoot, payload, currentFolderId]);
  console.log({ folderPayload, payload });
  const {
    data: folderItems = [],
    isFetching: isFetchingItemFolder,
    isError: isFolderError,
    error: folderError
  } = useAPSFolderItems(folderPayload);

  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const error = rootError || folderError;
    if (!error) return;
    console.log(error);
    // setErrorMessage(error?.raw.message || 'You do not have permission to access this Google Drive');
    setOpenError(true);
  }, [rootError, folderError]);

  const handleClose = () => setOpenError(false);

  useEffect(() => {
    console.log({ rootItems, folderItems });
  }, [rootItems, folderItems]);
  const itemsToRender = currentFolderId === 'root' ? rootItems : folderItems;

  const handleOpenFolder = (folder) => {
    console.log('open folder', folder);
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.attributes.displayName }]);
  };

  const handelSelectData = (data, param) => {
    onConfirm({ data, breadcrumb, param });
  };

  const handleBack = () => {
    if (breadcrumb.length <= 1) return;
    setBreadcrumb((prev) => prev.slice(0, -1));
  };

  const handleBreadcrumbNavigate = (index) => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
  };

  const isPageLoading = isFetchingFolder || isFetchingItemFolder;

  return (
    <>
      <Snackbar open={openError} onClose={handleClose} autoHideDuration={5000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{
            backgroundColor: '#fff',
            minWidth: 360,
            p: 2,
            boxShadow: 3,
            borderRadius: 1,
            borderLeft: '4px solid #d32f2f' // 🔥 error accent
          }}
          message={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%'
              }}
            >
              {/* Left icon */}
              <ErrorOutlineIcon
                sx={{
                  mr: 2,
                  fontSize: 28,
                  color: 'error.main'
                }}
              />

              {/* Center text */}
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" color="error.main">
                  Google Drive Error
                </Typography>
                <Typography variant="subtitle2" color="text.primary">
                  {errorMessage || 'Failed to load Google Drive data'}
                </Typography>
              </Box>

              {/* Right close button */}
              <IconButton size="small" onClick={handleClose} sx={{ ml: 2, color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          }
        />
      </Snackbar>
      <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1, fontWeight: 'bold' }} variant="h5">
              {data.project_name}
            </Typography>
            <Button color="inherit" onClick={onClose}>
              CLOSE
            </Button>
          </Toolbar>
        </AppBar>

        <Stack sx={{ height: '93%', bgcolor: 'background.paper', px: 4, py: 3 }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Tooltip title="Back">
              <span>
                <IconButton size="small" disabled={breadcrumb.length <= 1} onClick={handleBack}>
                  <ArrowBackIcon />
                </IconButton>
              </span>
            </Tooltip>

            <FolderBreadcrumb breadcrumb={breadcrumb} onNavigate={handleBreadcrumbNavigate} />
          </Stack>

          {/* Column header */}
          <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.5 }}>
            <Typography sx={{ flex: 1 }} fontWeight={500}>
              Name
            </Typography>
            <Typography sx={{ textAlign: 'right' }} fontWeight={500}>
              Action
            </Typography>
          </Stack>

          <Divider />
          {isPageLoading && (
            <Box
              sx={{
                width: '100%'
              }}
            >
              <LinearProgress />
            </Box>
          )}
          {/* List */}
          <Stack sx={{ flex: 1, overflowY: 'auto', mt: 1, pr: 1 }}>
            {itemsToRender.map((item) => (
              <FolderRow mode={folderMode} key={item.id} item={item} onOpen={handleOpenFolder} onSelect={handelSelectData} />
            ))}
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}
